import { Target } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'
import type { WinProbability } from '@/types'

// GRK-BRAND-5: probability colour per band — palette + WCAG-safe on white.
function getProbColor(prob: number): {
  color: string
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline'
} {
  if (prob >= 70) return { color: CATEGORICAL.evergreen.solid, badgeVariant: 'secondary' }
  if (prob >= 50) return { color: GRACKLE_COLORS.navy, badgeVariant: 'default' }
  if (prob >= 30) return { color: CATEGORICAL.clay.solid, badgeVariant: 'outline' }
  if (prob >= 15) return { color: CATEGORICAL.clay.solid, badgeVariant: 'outline' }
  return { color: GRACKLE_COLORS.darkRaspberry, badgeVariant: 'destructive' }
}

interface WinProbabilityBadgeProps {
  probability: number | null | undefined
  size?: 'sm' | 'md'
}

export function WinProbabilityBadge({ probability, size = 'sm' }: WinProbabilityBadgeProps) {
  if (probability == null) return null

  const { badgeVariant } = getProbColor(probability)

  return (
    <Badge
      variant={badgeVariant}
      className={size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5'}
      title={`Win probability: ${probability}%`}
      aria-label={`Win probability: ${probability} percent`}
    >
      {probability}%
    </Badge>
  )
}

interface WinProbabilityCardProps {
  winProbability: WinProbability | undefined
  isLoading?: boolean
}

export function WinProbabilityCard({ winProbability, isLoading }: WinProbabilityCardProps) {
  if (isLoading) {
    return (
      <div className="text-center p-4" role="status">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!winProbability) return null

  const { color } = getProbColor(winProbability.probability)
  const breakdownItems = [
    { key: 'stagePosition', label: 'Stage Position' },
    { key: 'velocity', label: 'Velocity' },
    { key: 'closeDate', label: 'Close Date' },
    { key: 'momentum', label: 'Momentum' },
  ] as const

  return (
    <>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm font-medium">Win Prob.</CardTitle>
        <Target className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </CardHeader>
      <CardContent className="p-3">
        <div
          className="text-xl font-bold leading-none"
          style={{ color }}
          aria-label={`Win probability: ${winProbability.probability} percent, ${winProbability.label}`}
        >
          {winProbability.probability}%
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="text-xs text-muted-foreground underline underline-offset-2 cursor-pointer hover:opacity-80 leading-tight"
              aria-label={`${winProbability.label}. Click for probability breakdown`}
            >
              {winProbability.label} &mdash; Details
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <p className="text-xs font-semibold mb-2">Probability Breakdown</p>
            <div className="space-y-1.5">
              {breakdownItems.map(({ key, label }) => {
                const item = winProbability.breakdown[key]
                return (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span
                      className="font-medium"
                      title={item.detail}
                      aria-label={`${label}: ${item.score} of ${item.max}. ${item.detail}`}
                    >
                      {item.score}/{item.max}
                    </span>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </CardContent>
    </>
  )
}
